/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import * as sjcl from 'sjcl';
import {Logger} from '@/util/logger';

/* the kinds of alerts */
export enum BlobType {
  String,
  File,
  Json,
  Folder,
  Plant,
  Traffic,
  Batch,
  BatchParam,
  Scenario,
  SimResult,
  Project,
  KPI,
  KPIX,
  KPITable,
  TPC,
  Schedule,
  Route,
  Forecast,
  Subdivision,
  None,
  CSV,
  Doc,
  PDF,
  SpreadSheet,
  Slides,
  Email,
  ZIP,
  GZip,
  BZip,
  Archive,
  Image,
  EXE,
  SQL,
  Disk,
  Audio,
  Video,
  PHP,
  Python,
  Shell,
  Java,
  JavaScript,
  TypeScript,
  CPlusPlus,
  Perl,
  XML,
  R,
  Ruby,
  Rust,
  MarkDown,
  YAML,
  CSS,
  Html,
  UserCommands,
  DiamondCrossings,
  NoExtension
}

/**
 *
 * @class Blob
 *
 * @brief front side model for a Blob the versioned file object we use in our virtaul file system
 * across containers (services)
 *
 * For SHA hashing, we are using the stanford implementation; its only temporary though, the
 * server side is what really does the hashing, we only need it in the front end for testing.
 *
 * @link https://bitwiseshiftleft.github.io/sjcl/
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class Blob {

  /** database Object ID of the blob (row id) */
  public         oid: number = 0;

  /** the blob system on disk path to the meta data */
  public        self: string = '';

  /** the blob system on disk path to the actual blob */
  public        data: string = '';

  /** the actual blob contents */
  public    contents: string = '';

  /** the blob size (bytes) */
  public        size: number = 0;

  /** the unique key (virtual path) of the blob */
  public         key: string = '';

  /** the UUID for the blob */
  public        guid: string = '';

  /** the human readable name for the blob (not unique) */
  public        name: string = '';

  /** the human readable description of the blob */
  public description: string = '';

  /** the kind of blob */
  public        type: BlobType = BlobType.None;

  /** the tags for the blob */
  public        tags: string[] = [];

  /** the commit SHA (if there is one) */
  public     version: string = '';

  /** the history instance of this snapshot */
  public    instance: string = '';

  /** the userid of the blob author/owner, different snapshots can have different owners */
  public       owner: string = '';

  /** the SHA hash for the blob contents */
  public         sha: string = '';

  /** creation date */
  public      create: string = '';

  /** last modified date */
  public    modified: string = '';

  /** fetch the file extension to use for the given kind of blob */
  public static extensionOfType(type: BlobType): string {

    switch (type) {
      case BlobType.String:           return '.str';
      case BlobType.File:             return '.fle';
      case BlobType.Json:             return '.json';
      case BlobType.Folder:           return '.fld';
      case BlobType.Plant:            return '.pla';
      case BlobType.Traffic:          return '.tra';
      case BlobType.Batch:            return '.bat';
      case BlobType.BatchParam:       return '.bp';
      case BlobType.Scenario:         return '.scn';
      case BlobType.SimResult:        return '.res';
      case BlobType.Project:          return '.pro';
      case BlobType.KPI:              return '.kpi';
      case BlobType.KPIX:             return '.kpix';
      case BlobType.KPITable:         return '.kpt';
      case BlobType.TPC:              return '.tpc';
      case BlobType.Schedule:         return '.sch';
      case BlobType.Route:            return '.ro';
      case BlobType.Subdivision:      return '.sub';
      case BlobType.Forecast:         return '.fc';
      case BlobType.CSV:              return '.csv';
      case BlobType.Doc:              return '.doc';
      case BlobType.PDF:              return '.pdf';
      case BlobType.SpreadSheet:      return '.xls';
      case BlobType.Slides:           return '.ppt';
      case BlobType.Email:            return '.email';
      case BlobType.ZIP:              return '.zip';
      case BlobType.GZip:             return '.gz';
      case BlobType.BZip:             return '.bz';
      case BlobType.Archive:          return '.tar';
      case BlobType.Image:            return '.jpg';
      case BlobType.EXE:              return '.exe';
      case BlobType.SQL:              return '.sql';
      case BlobType.Disk:             return '.iso';
      case BlobType.Audio:            return '.mp3';
      case BlobType.Video:            return '.mpg';
      case BlobType.PHP:              return '.php';
      case BlobType.Python:           return '.py';
      case BlobType.Shell:            return '.sh';
      case BlobType.Java:             return '.java';
      case BlobType.JavaScript:       return '.js';
      case BlobType.TypeScript:       return '.ts';
      case BlobType.CPlusPlus:        return '.cpp';
      case BlobType.Perl:             return '.pl';
      case BlobType.XML:              return '.xml';
      case BlobType.R:                return '.r';
      case BlobType.Ruby:             return '.rb';
      case BlobType.Rust:             return '.rs';
      case BlobType.MarkDown:         return '.md';
      case BlobType.YAML:             return '.yml';
      case BlobType.CSS:              return '.css';
      case BlobType.UserCommands:     return '.uc';
      case BlobType.DiamondCrossings: return '.dc';
    }

    return '';
  }

  /** give preference to actual type, unless None or File, then fallback to guessing */
  public static guessType(type: BlobType, key: string, name: string): BlobType {

    if (type !== BlobType.None && type !== BlobType.File) {

      /* we have a proper type */

      return type;
    }

    if (key !== '') {

      const t: BlobType = Blob.impliedType(key);

      if (t !== BlobType.None && t !== BlobType.File) {

        /* guessed from key */

        return t;
      }
    }

    if (name !== '') {

      const t: BlobType = Blob.impliedType(name);

      if (t !== BlobType.None && t !== BlobType.File) {

        /* guessed from name */

        return t;
      }
    }

    /* give up, pass back what they gave us */

    return type;
  }

  /** try to guess the type from the extension on the key or the name */
  public static impliedType(path): BlobType {

    /* first try basic types that should be pretty much native to our system */

    const ext = path.substr(path.lastIndexOf('.') + 1).trim().toLowerCase();

    switch (ext) {
      case 'str':
        return BlobType.String;
      case 'fle':
        return BlobType.File;
      case 'fld':
        return BlobType.Folder;
      case 'pla':
        return BlobType.Plant;
      case 'tra':
        return BlobType.Traffic;
      case 'bat':
        return BlobType.Batch;
      case 'bp':
        return BlobType.BatchParam;
      case 'scn':
        return BlobType.Scenario;
      case 'res':
        return BlobType.SimResult;
      case 'pro':
        return BlobType.Project;
      case 'prj':
        return BlobType.Project;
      case 'kpi':
        return BlobType.KPI;
      case 'kpix':
        return BlobType.KPIX;
      case 'kpt':
        return BlobType.KPITable;
      case 'tpc':
        return BlobType.TPC;
      case 'sch':
        return BlobType.Schedule;
      case 'ro':
        return BlobType.Route;
      case 'sub':
        return BlobType.Subdivision;
      case 'fc':
        return BlobType.Forecast;
      case 'json':
        return BlobType.Json;
    }

    /*
     * ok, now the external file types, that we don't normally handle, but lets try to gracefully handle
     * as much as we can.
     *
     */

    let extMap: any = {
      csv:      BlobType.CSV,
      doc:      BlobType.Doc,
      docm:     BlobType.Doc,
      docx:     BlobType.Doc,
      mht:      BlobType.Doc,
      mhtl:     BlobType.Doc,
      odt:      BlobType.Doc,
      rtf:      BlobType.Doc,
      txt:      BlobType.Doc,
      wps:      BlobType.Doc,
      xps:      BlobType.Doc,
      pdf:      BlobType.PDF,
      dif:      BlobType.SpreadSheet,
      ods:      BlobType.SpreadSheet,
      prn:      BlobType.SpreadSheet,
      slk:      BlobType.SpreadSheet,
      xla:      BlobType.SpreadSheet,
      xlam:     BlobType.SpreadSheet,
      xls:      BlobType.SpreadSheet,
      xlsb:     BlobType.SpreadSheet,
      xlsm:     BlobType.SpreadSheet,
      xlsx:     BlobType.SpreadSheet,
      xlt:      BlobType.SpreadSheet,
      xltm:     BlobType.SpreadSheet,
      xltx:     BlobType.SpreadSheet,
      xlw:      BlobType.SpreadSheet,
      emf:      BlobType.Slides,
      odp:      BlobType.Slides,
      pot:      BlobType.Slides,
      potm:     BlobType.Slides,
      potx:     BlobType.Slides,
      ppa:      BlobType.Slides,
      ppam:     BlobType.Slides,
      pps:      BlobType.Slides,
      ppsm:     BlobType.Slides,
      ppsx:     BlobType.Slides,
      ppt:      BlobType.Slides,
      pptm:     BlobType.Slides,
      pptx:     BlobType.Slides,
      email:    BlobType.Email,
      eml:      BlobType.Email,
      emlx:     BlobType.Email,
      msg:      BlobType.Email,
      oft:      BlobType.Email,
      ost:      BlobType.Email,
      pst:      BlobType.Email,
      vcf:      BlobType.Email,
      zip:      BlobType.ZIP,
      arj:      BlobType.ZIP,
      deb:      BlobType.ZIP,
      pkg:      BlobType.ZIP,
      rar:      BlobType.ZIP,
      rpm:      BlobType.ZIP,
      z:        BlobType.ZIP,
      gz:       BlobType.GZip,
      bz:       BlobType.BZip,
      tar:      BlobType.Archive,
      jar:      BlobType.Archive,
      msi:      BlobType.Archive,
      ai:       BlobType.Image,
      bmp:      BlobType.Image,
      gif:      BlobType.Image,
      ico:      BlobType.Image,
      jpeg:     BlobType.Image,
      jpg:      BlobType.Image,
      png:      BlobType.Image,
      psd:      BlobType.Image,
      svg:      BlobType.Image,
      tif:      BlobType.Image,
      tiff:     BlobType.Image,
      webp:     BlobType.Image,
      apk:      BlobType.EXE,
      bat:      BlobType.EXE,
      com:      BlobType.EXE,
      exe:      BlobType.EXE,
      gadget:   BlobType.EXE,
      wsf:      BlobType.EXE,
      dat:      BlobType.SQL,
      db:       BlobType.SQL,
      dbf:      BlobType.SQL,
      mdb:      BlobType.SQL,
      sav:      BlobType.SQL,
      sql:      BlobType.SQL,
      bin:      BlobType.Disk,
      dmg:      BlobType.Disk,
      iso:      BlobType.Disk,
      toast:    BlobType.Disk,
      vcd:      BlobType.Disk,
      aif:      BlobType.Audio,
      cda:      BlobType.Audio,
      mid:      BlobType.Audio,
      mp3:      BlobType.Audio,
      mpa:      BlobType.Audio,
      ogg:      BlobType.Audio,
      wav:      BlobType.Audio,
      wma:      BlobType.Audio,
      wpl:      BlobType.Audio,
      avi:      BlobType.Video,
      flv:      BlobType.Video,
      h264:     BlobType.Video,
      m4v:      BlobType.Video,
      mkv:      BlobType.Video,
      mov:      BlobType.Video,
      mp4:      BlobType.Video,
      mpg:      BlobType.Video,
      mpeg:     BlobType.Video,
      rm:       BlobType.Video,
      swf:      BlobType.Video,
      vob:      BlobType.Video,
      wmv:      BlobType.Video,
      md:       BlobType.MarkDown,
      markdown: BlobType.MarkDown,
      mkd:      BlobType.MarkDown,
      mkdn:     BlobType.MarkDown,
      mkdown:   BlobType.MarkDown,
      ron:      BlobType.MarkDown,
      xml:      BlobType.XML,
      ant:      BlobType.XML,
      gml:      BlobType.XML,
      grxml:    BlobType.XML,
      iml:      BlobType.XML,
      ivy:      BlobType.XML,
      jelly:    BlobType.XML,
      kml:      BlobType.XML,
      rdf:      BlobType.XML,
      rss:      BlobType.XML,
      scxml:    BlobType.XML,
      tml:      BlobType.XML,
      vxml:     BlobType.XML,
      wsdl:     BlobType.XML,
      xaml:     BlobType.XML,
      xmi:      BlobType.XML,
      xsd:      BlobType.XML,
      xul:      BlobType.XML,
      r:        BlobType.R,
      rd:       BlobType.R,
      rs:       BlobType.Rust,
      rb:       BlobType.Ruby,
      rabl:     BlobType.Ruby,
      rake:     BlobType.Ruby,
      rbuild:   BlobType.Ruby,
      rbw:      BlobType.Ruby,
      rbx:      BlobType.Ruby,
      ru:       BlobType.Ruby,
      ruby:     BlobType.Ruby,
      thor:     BlobType.Ruby,
      watchr:   BlobType.Ruby,
      cpp:      BlobType.CPlusPlus,
      cc:       BlobType.CPlusPlus,
      cp:       BlobType.CPlusPlus,
      cxx:      BlobType.CPlusPlus,
      h:        BlobType.CPlusPlus,
      hh:       BlobType.CPlusPlus,
      hpp:      BlobType.CPlusPlus,
      hxx:      BlobType.CPlusPlus,
      inc:      BlobType.CPlusPlus,
      inl:      BlobType.CPlusPlus,
      ipp:      BlobType.CPlusPlus,
      tcc:      BlobType.CPlusPlus,
      tpp:      BlobType.CPlusPlus,
      pl:       BlobType.Perl,
      cgi:      BlobType.Perl,
      fcgi:     BlobType.Perl,
      perl:     BlobType.Perl,
      ph:       BlobType.Perl,
      plx:      BlobType.Perl,
      pm:       BlobType.Perl,
      pod:      BlobType.Perl,
      psgi:     BlobType.Perl,
      t:        BlobType.Perl,
      sh:       BlobType.Shell,
      bash:     BlobType.Shell,
      bats:     BlobType.Shell,
      command:  BlobType.Shell,
      ksh:      BlobType.Shell,
      tool:     BlobType.Shell,
      zsh:      BlobType.Shell,
      yml:      BlobType.YAML,
      reek:     BlobType.YAML,
      rviz:     BlobType.YAML,
      syntax:   BlobType.YAML,
      yaml:     BlobType.YAML,
      css:      BlobType.CSS,
      html:     BlobType.Html,
      htm:      BlobType.Html,
      uc:       BlobType.UserCommands,
      dc:       BlobType.DiamondCrossings,
      st:       BlobType.Html,
      xht:      BlobType.Html,
      xhtml:    BlobType.Html,
      php:      BlobType.PHP,
      aw:       BlobType.PHP,
      ctp:      BlobType.PHP,
      php3:     BlobType.PHP,
      php4:     BlobType.PHP,
      php5:     BlobType.PHP,
      phps:     BlobType.PHP,
      phpt:     BlobType.PHP,
      py:       BlobType.Python,
      pyde:     BlobType.Python,
      pyp:      BlobType.Python,
      pyt:      BlobType.Python,
      pyw:      BlobType.Python,
      rpy:      BlobType.Python,
      tac:      BlobType.Python,
      wsgi:     BlobType.Python,
      xpy:      BlobType.Python,
      java:     BlobType.Java,
      jsp:      BlobType.Java,
      js:       BlobType.JavaScript,
      _js:      BlobType.JavaScript,
      es:       BlobType.JavaScript,
      es6:      BlobType.JavaScript,
      frag:     BlobType.JavaScript,
      jake:     BlobType.JavaScript,
      jsb:      BlobType.JavaScript,
      jsm:      BlobType.JavaScript,
      jss:      BlobType.JavaScript,
      njc:      BlobType.JavaScript,
      pac:      BlobType.JavaScript,
      xsjs:     BlobType.JavaScript,
      jsjslib:  BlobType.JavaScript,
      ts:       BlobType.TypeScript,
      tsx:      BlobType.TypeScript
    };

    extMap['7z']  = BlobType.ZIP;
    extMap['3g2'] = BlobType.Video;
    extMap['3gp'] = BlobType.Video;
    extMap['c++'] = BlobType.CPlusPlus;
    extMap['h++'] = BlobType.CPlusPlus;

    if (extMap.hasOwnProperty(ext)) {

      /* we found an exact extension match to a file type */

      return extMap[ext];
    }

    /* if we fall through, we couldn't match anything */

    return BlobType.None;
  }

  /** fetch the icon to use for the given blob type */
  public static iconOfType(type: BlobType) {

    switch (type) {
      case BlobType.String:           return 'blob-str-20.png';
      case BlobType.File:             return 'blob-file-20.png';
      case BlobType.Json:             return 'file-json-20.png';
      case BlobType.Folder:           return 'blob-folder-20.png';
      case BlobType.Plant:            return 'blob-plant-20.png';
      case BlobType.Traffic:          return 'blob-traffic-20.png';
      case BlobType.Batch:            return 'blob-batch-20.png';
      case BlobType.BatchParam:       return 'blob-batchparm-20.png';
      case BlobType.Scenario:         return 'blob-scenario-20.png';
      case BlobType.SimResult:        return 'blob-simresult-20.png';
      case BlobType.Project:          return 'blob-project-20.png';
      case BlobType.KPI:              return 'blob-kpi-20.png';
      case BlobType.KPIX:             return 'blob-kpix-20.png';
      case BlobType.KPITable:         return 'blob-kpi-20.png';
      case BlobType.TPC:              return 'blob-tpc-20.png';
      case BlobType.Schedule:         return 'blob-schedule-20.png';
      case BlobType.Route:            return 'blob-route-20.png';
      case BlobType.Subdivision:      return 'blob-subdivision-20.png';
      case BlobType.Forecast:         return 'blob-forecast-20.png';
      case BlobType.CSV:              return 'blob-csv-20.png';
      case BlobType.Doc:              return 'blob-doc-20.png';
      case BlobType.PDF:              return 'blob-pdf-20.png';
      case BlobType.SpreadSheet:      return 'blob-spreadsheet-20.png';
      case BlobType.Slides:           return 'blob-slides-20.png';
      case BlobType.Email:            return 'blob-email-20.png';
      case BlobType.ZIP:              return 'blob-zip-20.png';
      case BlobType.GZip:             return 'blob-gzip-20.png';
      case BlobType.BZip:             return 'blob-bzip-20.png';
      case BlobType.Archive:          return 'blob-archive-20.png';
      case BlobType.Image:            return 'blob-image-20.png';
      case BlobType.EXE:              return 'blob-exe-20.png';
      case BlobType.SQL:              return 'blob-sql-20.png';
      case BlobType.Disk:             return 'blob-disk-20.png';
      case BlobType.Audio:            return 'blob-audio-20.png';
      case BlobType.Video:            return 'blob-video-20.png';
      case BlobType.PHP:              return 'blob-php-20.png';
      case BlobType.Python:           return 'blob-python-20.png';
      case BlobType.Shell:            return 'blob-shell-20.png';
      case BlobType.Java:             return 'blob-java-20.png';
      case BlobType.JavaScript:       return 'blob-javascript-20.png';
      case BlobType.TypeScript:       return 'blob-typescript-20.png';
      case BlobType.CPlusPlus:        return 'blob-cplusplus-20.png';
      case BlobType.Perl:             return 'blob-perl-20.png';
      case BlobType.XML:              return 'blob-xml-20.png';
      case BlobType.R:                return 'blob-r-20.png';
      case BlobType.Ruby:             return 'blob-ruby-20.png';
      case BlobType.Rust:             return 'blob-rust-20.png';
      case BlobType.MarkDown:         return 'blob-markdown-20.png';
      case BlobType.YAML:             return 'blob-yaml-20.png';
      case BlobType.CSS:              return 'blob-css-20.png';
      case BlobType.Html:             return 'blob-html-20.png';
      case BlobType.UserCommands:     return 'blob-cmd-20.png';
      case BlobType.DiamondCrossings: return 'blob-diamond-20.png';
    }

    return 'blob-file-20.png';
  }

  /** convert blob type to string */
  public static stringOfType(type: BlobType) {

    switch (type) {
      case BlobType.String:           return 'string';
      case BlobType.File:             return 'file';
      case BlobType.Json:             return 'json';
      case BlobType.Folder:           return 'folder';
      case BlobType.Plant:            return 'plant';
      case BlobType.Traffic:          return 'traffic';
      case BlobType.Batch:            return 'batch';
      case BlobType.BatchParam:       return 'batch_parameters';
      case BlobType.Scenario:         return 'scenario';
      case BlobType.SimResult:        return 'sim_result';
      case BlobType.Project:          return 'project';
      case BlobType.KPI:              return 'kpi';
      case BlobType.KPIX:             return 'kpix';
      case BlobType.KPITable:         return 'kpt';
      case BlobType.TPC:              return 'tpc';
      case BlobType.Schedule:         return 'schedule';
      case BlobType.Route:            return 'route';
      case BlobType.Subdivision:      return 'subdivision';
      case BlobType.Forecast:         return 'forecast';
      case BlobType.None:             return 'none';
      case BlobType.CSV:              return 'csv';
      case BlobType.Doc:              return 'doc';
      case BlobType.PDF:              return 'pdf';
      case BlobType.SpreadSheet:      return 'spreadsheet';
      case BlobType.Slides:           return 'slides';
      case BlobType.Email:            return 'email';
      case BlobType.ZIP:              return 'zip';
      case BlobType.GZip:             return 'gzip';
      case BlobType.BZip:             return 'bzip';
      case BlobType.Archive:          return 'archive';
      case BlobType.Image:            return 'image';
      case BlobType.EXE:              return 'exe';
      case BlobType.SQL:              return 'sql';
      case BlobType.Disk:             return 'disk';
      case BlobType.Audio:            return 'audio';
      case BlobType.Video:            return 'video';
      case BlobType.PHP:              return 'php';
      case BlobType.Python:           return 'python';
      case BlobType.Shell:            return 'shell';
      case BlobType.Java:             return 'java';
      case BlobType.JavaScript:       return 'javascript';
      case BlobType.TypeScript:       return 'typescript';
      case BlobType.CPlusPlus:        return 'cplusplus';
      case BlobType.Perl:             return 'perl';
      case BlobType.XML:              return 'xml';
      case BlobType.R:                return 'r';
      case BlobType.Ruby:             return 'ruby';
      case BlobType.Rust:             return 'rust';
      case BlobType.MarkDown:         return 'markdown';
      case BlobType.YAML:             return 'yaml';
      case BlobType.CSS:              return 'css';
      case BlobType.Html:             return 'html';
      case BlobType.UserCommands:     return 'uc';
      case BlobType.DiamondCrossings: return 'dc';
    }

    return 'none';
  }

  /** convert blob type string to blob type */
  public static typeofString(text: string) {

    text = text.toLowerCase().trim();

    switch (text) {
      case 'string':           return BlobType.String;
      case 'file':             return BlobType.File;
      case 'json':             return BlobType.Json;
      case 'folder':           return BlobType.Folder;
      case 'plant':            return BlobType.Plant;
      case 'traffic':          return BlobType.Traffic;
      case 'batch':            return BlobType.Batch;
      case 'batch_parameters': return BlobType.BatchParam;
      case 'scenario':         return BlobType.Scenario;
      case 'sim_result':       return BlobType.SimResult;
      case 'project':          return BlobType.Project;
      case 'kpi':              return BlobType.KPI;
      case 'kpix':             return BlobType.KPIX;
      case 'kpt':              return BlobType.KPITable;
      case 'tpc':              return BlobType.TPC;
      case 'schedule':         return BlobType.Schedule;
      case 'route':            return BlobType.Route;
      case 'subdivision':      return BlobType.Subdivision;
      case 'forecast':         return BlobType.Forecast;
      case 'none':             return BlobType.None;
      case 'csv':              return BlobType.CSV;
      case 'doc':              return BlobType.Doc;
      case 'pdf':              return BlobType.PDF;
      case 'spreadsheet':      return BlobType.SpreadSheet;
      case 'slides':           return BlobType.Slides;
      case 'email':            return BlobType.Email;
      case 'zip':              return BlobType.ZIP;
      case 'gzip':             return BlobType.GZip;
      case 'bzip':             return BlobType.BZip;
      case 'archive':          return BlobType.Archive;
      case 'image':            return BlobType.Image;
      case 'exe':              return BlobType.EXE;
      case 'sql':              return BlobType.SQL;
      case 'disk':             return BlobType.Disk;
      case 'audio':            return BlobType.Audio;
      case 'video':            return BlobType.Video;
      case 'php':              return BlobType.PHP;
      case 'python':           return BlobType.Python;
      case 'shell':            return BlobType.Shell;
      case 'java':             return BlobType.Java;
      case 'javascript':       return BlobType.JavaScript;
      case 'typescript':       return BlobType.TypeScript;
      case 'cplusplus':        return BlobType.CPlusPlus;
      case 'perl':             return BlobType.Perl;
      case 'xml':              return BlobType.XML;
      case 'r':                return BlobType.R;
      case 'ruby':             return BlobType.Ruby;
      case 'rust':             return BlobType.Rust;
      case 'markdown':         return BlobType.MarkDown;
      case 'yaml':             return BlobType.YAML;
      case 'css':              return BlobType.CSS;
      case 'html':             return BlobType.Html;
      case 'uc':               return BlobType.UserCommands;
      case 'dc':               return BlobType.DiamondCrossings;
    }

    return BlobType.None;
  }

  /** standard constructor */
  constructor() {

  }

  private static hash(text: string) {

    return sjcl.codec.hex.fromBits(sjcl.hash.sha256.hash(text));
  }

  /** set blob content from object (for front side testing) */
  public setFromObject(thing: any) {

    const text: string = JSON.stringify(thing);

    Logger.debug(`[Blob] setting blob (${this.key}) from object:`, thing);

    return this.setFromString(text);
  }

  /** asynchronously set the content from a file on disk */
  public setFromFile(file: File): Promise<any> {

    /*
     * lots of butt hurt here.  We have to do this asynchronously because if they click on a 10GB
     * file, we will lock up the browser, if we try to do it synchronously.  This has causes grief
     * though, because the surrounding functions expect the blob to be updated upon return.
     *
     * It gets worse; some of those function (in fact the main users of this method) are used in
     * our Http request interception, which has to return an observable as the http response.
     *
     * So...to fix everything at once, we wrap the file read in a promise and return that...then
     * at the http request/response level, we use RxJs operators to convert / pipe the promise
     * into an observable that is usable to the outside world making fake Http requests.
     *
     */

    /* get the contents of the file */

    const reader     = new FileReader();
    const blob: Blob = this;

    return new Promise((resolveBlob, rejectBlob) => {

      reader.onload = () =>  {

        const  text: string = reader.result as string;

        Logger.debug(`[Blob] setting blob (${blob.key}) from file:`, file);

        /* setup as usual */

        blob.size     = file.size;
        blob.sha      = Blob.hash(text);
        blob.contents = text;

        blob.modified = new Date().toISOString().slice(0, 19).replace('T', ' ');

        Logger.debug(`[Blob] resolving...`);
        resolveBlob(blob);

      };

      reader.onerror = (e) => {

        Logger.debug(`[Blob] rejecting...`);
        rejectBlob(e.target.error);

      };

      reader.readAsText(file);

    });

  }

  /** set blob content from string (for front side testing) */
  public setFromString(text: string) {

    Logger.debug(`[Blob] setting blob (${this.key}) from string:`, text);

    this.size     = text.length;
    this.sha      = Blob.hash(text);
    this.contents = text;

    this.modified = new Date().toISOString().slice(0, 19).replace('T', ' ');
  }

  /** grab raw contents */
  public rawContent() {

    return this.contents;
  }

  /** grab contents as an object */
  public parsedContent() : any {

    return JSON.parse(this.contents);
  }

  /** restore from string */
  public deserialze(text: string) : boolean {

    const  data: any = JSON.parse(text);

    this.oid         = data.oid;
    this.self        = data.self;
    this.data        = data.data;
    this.contents    = data.contents;
    this.size        = data.size;
    this.key         = data.key;
    this.guid        = data.guid;
    this.name        = data.name;
    this.description = data.description;
    this.type        = data.type;
    this.tags        = data.tags;
    this.version     = data.version;
    this.instance    = data.instance;
    this.owner       = data.owner;
    this.sha         = data.sha;
    this.create      = data.create;
    this.modified    = data.modified;

    return true;
  }

  /** serialize for saving */
  public serialize() {

    return JSON.stringify({
      oid:         this.oid,
      self:        this.self,
      data:        this.data,
      contents:    this.contents,
      size:        this.size,
      key:         this.key,
      guid:        this.guid,
      name:        this.name,
      description: this.description,
      type:        this.type,
      tags:        this.tags,
      version:     this.version,
      instance:    this.instance,
      owner:       this.owner,
      sha:         this.sha,
      create:      this.create,
      modified:    this.modified
    });
  }

}
